import React, { useCallback, useEffect, useRef, useState } from "react";

/**
 * The lifecycle segment of the club's request list: Active (n) · Drafts (n).
 *
 * It renders ONLY under the "My Requests" source. On "All Listed Requests" and
 * on "Saved" it does not exist at all, because neither of those lists is the
 * club's own row set and neither has a draft.
 *
 * WHAT IS REUSED, AND FROM WHERE
 * ------------------------------
 * The pill LOOK is the page's own, lifted verbatim from the category pills in
 * components/club/TenderTabs.js:95-99 — active is the #2283FF outline on the
 * #E9F3FF fill, inactive is the #E6E6E6 outline on white. Nothing new was
 * invented for it.
 *
 * The strip BEHAVIOUR is the one the platform already proved on the pitches
 * page, components/viewpitches/ViewPitchesTabs.js:39-85: the row scrolls
 * horizontally, the pill that was just activated is scrolled into view inside
 * the strip (scrollTo, never scrollIntoView, which would scroll the page and
 * yank the list out from under the thumb), a fade edge says the row continues,
 * and the row can be dragged. That page kept its own component because it is
 * welded to PITCH_TABS and to a tender's pitch counters; this one is a plain
 * list of pills, so the mechanics were reused rather than the component.
 *
 * Tap targets are min-h-[44px] — larger than the 36px category pills, because
 * this row is new and the platform's target rule applies to it.
 *
 * Counters come from the server (`lifecycle_counts` on the facet endpoint) and
 * are never counted up in the browser. A count that has not arrived yet is
 * null, and the pill then renders its label alone instead of a wrong "0".
 *
 * `variant`: "row" (default) is the segment's own white row with the page
 * gutters — the phone and small-desktop layout. "inline" drops that shell so
 * the segment can sit INSIDE the category-tab row from 1440px up (Marin's
 * header ticket): the host row brings background, padding and border, the
 * segment only brings its pills and its scroll behaviour.
 */
const LifecycleSegment = ({ tabs, activeTab, onChange, variant = "row" }) => {
  const stripRef = useRef(null);
  const dragRef = useRef(null);
  const suppressClickRef = useRef(false);
  const [showFade, setShowFade] = useState(false);

  const measure = useCallback(() => {
    const el = stripRef.current;
    if (!el) return;
    setShowFade(el.scrollWidth > el.clientWidth + 4);
  }, []);

  useEffect(() => {
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [measure]);

  // Keep the ACTIVE pill visible without touching the page scroll — the rule
  // the pitches strip established after the 375px measurement showed pills at
  // 0% visibility.
  const scrollToPill = (el) => {
    const strip = stripRef.current;
    if (!strip || !el) return;
    const target = Math.max(0, el.offsetLeft - 16);
    if (typeof strip.scrollTo === "function") {
      try {
        strip.scrollTo({ left: target, behavior: "smooth" });
        return;
      } catch (err) {
        // Older engines reject the options object; fall through.
      }
    }
    strip.scrollLeft = target;
  };

  const onPointerDown = (event) => {
    const strip = stripRef.current;
    if (!strip) return;
    dragRef.current = { x: event.clientX, left: strip.scrollLeft, moved: 0 };
  };

  const onPointerMove = (event) => {
    const strip = stripRef.current;
    const drag = dragRef.current;
    if (!drag || !strip) return;
    const dx = event.clientX - drag.x;
    if (Math.abs(dx) > 3) {
      drag.moved = Math.max(drag.moved, Math.abs(dx));
      strip.scrollLeft = drag.left - dx;
    }
  };

  const endDrag = () => {
    if (dragRef.current && dragRef.current.moved > 8) {
      suppressClickRef.current = true;
    }
    dragRef.current = null;
  };

  const select = (id, el) => {
    if (suppressClickRef.current) {
      suppressClickRef.current = false;
      return;
    }
    onChange(id);
    scrollToPill(el);
  };

  // The active pill is scrolled into view on mount too, so arriving on Drafts
  // from a deep link does not leave the row parked on Active at 375px.
  useEffect(() => {
    const strip = stripRef.current;
    if (!strip) return;
    const el = strip.querySelector('[data-active="true"]');
    if (el) scrollToPill(el);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  return (
    <div
      className={
        variant === "inline"
          ? "relative min-w-0 flex-1"
          : "relative bg-white px-4 md:px-12 pt-4 pb-1"
      }
    >
      <div
        ref={stripRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerLeave={endDrag}
        role="tablist"
        aria-label="Request lifecycle"
        className="flex flex-nowrap gap-2 overflow-x-auto overflow-y-hidden px-0.5 py-1 [scrollbar-width:none] [touch-action:pan-x] cursor-grab"
      >
        {tabs.map((tab) => {
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={isActive}
              data-active={isActive ? "true" : "false"}
              onClick={(event) => select(tab.id, event.currentTarget)}
              className={`inline-flex min-h-[44px] flex-none items-center whitespace-nowrap rounded-full px-4 text-sm ${
                isActive
                  ? "border border-[#2283FF] text-[#2283FF] bg-[#E9F3FF] font-medium"
                  : "border border-[#E6E6E6] text-[#1E1E1ECC] font-normal"
              }`}
            >
              {tab.count == null ? tab.label : `${tab.label} (${tab.count})`}
            </button>
          );
        })}
      </div>
      {showFade ? (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-y-0 right-0 w-14 bg-gradient-to-r from-transparent to-white"
        />
      ) : null}
    </div>
  );
};

export default LifecycleSegment;
